import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';
import { formatDate } from '../../lib/utils';
import SafeIcon from '../../common/SafeIcon';
import { FiClock, FiAlertTriangle } from 'react-icons/fi';

const MAX_VISITS = 5;

const describe = (f) =>
  [f.manufacturer?.name, f.model?.model_name, f.caliber?.name].filter(Boolean).join(' · ');

// Shown under the serial number on Acquire Firearm: earlier bound book entries
// for the same serial, so a returning firearm is recognized and its details
// can be reused. A firearm still in house can't be acquired again (the
// database rejects it); this warns before the user fills in the rest.
export default function FirearmHistoryNotice({ tenantId, serialNumber, onUseDetails }) {
  const [visits, setVisits] = useState([]);

  useEffect(() => {
    const serial = (serialNumber || '').trim();
    if (!tenantId || serial.length < 3) {
      setVisits([]);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const data = await api.getFirearmHistory(tenantId, serial);
        if (!cancelled) setVisits(data);
      } catch {
        if (!cancelled) setVisits([]);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [tenantId, serialNumber]);

  if (visits.length === 0) return null;

  const inHouse = visits.filter((v) => v.status === 'IN_HOUSE');
  const latest = visits[0];

  return (
    <div
      className={`rounded-md border p-4 text-sm ${
        inHouse.length > 0
          ? 'border-yellow-300 bg-yellow-50 text-yellow-900 dark:border-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-200'
          : 'border-primary-200 bg-primary-50 text-slate-700 dark:border-primary-800 dark:bg-primary-900/20 dark:text-slate-300'
      }`}
    >
      <div className="flex items-start gap-2">
        <SafeIcon icon={inHouse.length > 0 ? FiAlertTriangle : FiClock} className="mt-0.5 h-4 w-4 shrink-0" />
        <div className="flex-1 space-y-2">
          <p className="font-medium">
            {inHouse.length > 0
              ? 'This serial number is currently in house.'
              : `This serial number has been in the bound book before (${visits.length} ${visits.length === 1 ? 'entry' : 'entries'}).`}
          </p>
          {inHouse.length > 0 && (
            <p className="text-xs">
              The same manufacturer and serial can't be acquired again until its current entry has a disposition.
              A different firearm that shares this serial from another manufacturer is fine.
            </p>
          )}
          <ul className="space-y-1 text-xs">
            {visits.slice(0, MAX_VISITS).map((v) => (
              <li key={v.id}>
                <span className="font-medium">{describe(v.firearm) || 'Unknown firearm'}</span>
                {' — '}acquired {formatDate(v.acquisition_date)}
                {v.status === 'IN_HOUSE' ? ', in house' : `, disposed ${formatDate(v.disposition_date)}`}
              </li>
            ))}
            {visits.length > MAX_VISITS && <li>…and {visits.length - MAX_VISITS} earlier</li>}
          </ul>
          {onUseDetails && (
            <button
              type="button"
              onClick={() => onUseDetails(latest.firearm)}
              className="text-xs font-medium text-primary-600 hover:underline dark:text-primary-400"
            >
              Use details from the most recent entry
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
